import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { ROLES } from 'config/roles';
import { PATHS } from 'routes/routePaths';

import ProtectedRoute from 'routes/ProtectedRoute';
import RoleRoute from 'routes/RoleRoute';
import PublicOnlyRoute from 'routes/PublicOnlyRoute';

// Layouts + guards stay eager: they're small, render immediately, and sit on
// the critical path for every route. Page components below are code-split with
// React.lazy so each role area (and the public/auth screens) ships as its own
// chunk — the initial bundle only loads the page actually being viewed.
import RootLayout from 'layouts/RootLayout';
import AuthLayout from 'layouts/AuthLayout';
import StudentLayout from 'layouts/StudentLayout';
import MentorLayout from 'layouts/MentorLayout';
import AdminLayout from 'layouts/AdminLayout';

import { LoadingScreen } from 'components';

// Public pages
const LandingPage = lazy(() => import('features/public/pages/LandingPage'));
const NotFoundPage = lazy(() => import('features/public/pages/NotFoundPage'));
const UnauthorizedPage = lazy(() => import('features/public/pages/UnauthorizedPage'));

// Auth pages. Public sign-up is disabled — /register redirects to /login.
const LoginPage = lazy(() => import('features/auth/pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('features/auth/pages/ForgotPasswordPage'));

// Role dashboards
const StudentDashboard = lazy(() => import('features/student/pages/StudentDashboard'));
const StudentClasses = lazy(() => import('features/student/pages/StudentClasses'));
const StudentCalendar = lazy(() => import('features/student/pages/StudentCalendar'));
const StudentProgress = lazy(() => import('features/student/pages/StudentProgress'));
const StudentRecords = lazy(() => import('features/student/pages/StudentRecords'));
const StudentMentor = lazy(() => import('features/student/pages/StudentMentor'));
const StudentAvailability = lazy(() => import('features/student/pages/StudentAvailability'));
const StudentRequests = lazy(() => import('features/student/pages/StudentRequests'));
// Portfolio — self-contained "My Projects" module (student-authored projects).
const StudentPortfolio = lazy(() => import('features/portfolio/pages/StudentPortfolio'));
const MentorPortfolioReview = lazy(() => import('features/portfolio/pages/MentorPortfolioReview'));
// Public (unauthenticated) portfolio pages — shareable, noindex.
const PublicPortfolio = lazy(() => import('features/portfolio/pages/PublicPortfolio'));
const PublicProject = lazy(() => import('features/portfolio/pages/PublicProject'));
// Shared study-material library — same page serves the student and mentor areas.
const ResourcesPage = lazy(() => import('features/resources/pages/ResourcesPage'));
// Join redirect — stamps the caller's join time, then forwards to the meeting.
const JoinClassPage = lazy(() => import('features/scheduling/pages/JoinClassPage'));
const MentorDashboard = lazy(() => import('features/mentor/pages/MentorDashboard'));
const MentorStudents = lazy(() => import('features/mentor/pages/MentorStudents'));
const MentorStudentDetail = lazy(() => import('features/mentor/pages/MentorStudentDetail'));
const MentorClasses = lazy(() => import('features/mentor/pages/MentorClasses'));
const MentorCalendar = lazy(() => import('features/mentor/pages/MentorCalendar'));
const MentorAvailability = lazy(() => import('features/mentor/pages/MentorAvailability'));
const MentorOpenSlots = lazy(() => import('features/mentor/pages/MentorOpenSlots'));
const MentorRecords = lazy(() => import('features/mentor/pages/MentorRecords'));
const MentorRequests = lazy(() => import('features/mentor/pages/MentorRequests'));
const MentorPayouts = lazy(() => import('features/payouts/pages/MentorPayouts'));
const AdminDashboard = lazy(() => import('features/admin/pages/AdminDashboard'));
const ManageStudents = lazy(() => import('features/admin/pages/ManageStudents'));
const AdminStudentDetail = lazy(() => import('features/admin/pages/StudentDetail'));
const ManageMentors = lazy(() => import('features/admin/pages/ManageMentors'));
const AccountsPage = lazy(() => import('features/admin/pages/AccountsPage'));
const ManageCourses = lazy(() => import('features/admin/pages/ManageCourses'));
const ManagePresentations = lazy(() => import('features/admin/pages/ManagePresentations'));
const ManageEnrollments = lazy(() => import('features/admin/pages/ManageEnrollments'));
const AssignMentor = lazy(() => import('features/admin/pages/AssignMentor'));
const EnrolStudent = lazy(() => import('features/admin/pages/EnrolStudent'));
const ScheduleClass = lazy(() => import('features/admin/pages/ScheduleClass'));
const RescheduleClass = lazy(() => import('features/admin/pages/RescheduleClass'));
const CalendarPage = lazy(() => import('features/admin/pages/CalendarPage'));
const SessionLogsPage = lazy(() => import('features/admin/pages/SessionLogsPage'));
const DisputesPage = lazy(() => import('features/admin/pages/DisputesPage'));
const ReportsPage = lazy(() => import('features/admin/pages/ReportsPage'));
const SchedulingSettings = lazy(() => import('features/admin/pages/SchedulingSettings'));

/**
 * Single source of truth for the route tree.
 *
 * Layering (outer → inner):
 *   RootLayout            chrome shared by everything (toaster, error UI)
 *     ├─ public routes    open to anyone
 *     ├─ PublicOnlyRoute  auth screens, redirect away if logged in
 *     └─ ProtectedRoute   requires a session
 *          └─ RoleRoute   requires a specific role  →  {Role}Layout
 *
 * Each role's bare root (e.g. /mentor) redirects to its dashboard so links
 * and ROLE_HOME stay tidy, while RoleRoute guarantees a user can only ever
 * reach the dashboard their role permits — everyone else is sent to /unauthorized.
 */
export default function AppRouter() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
      <Route element={<RootLayout />}>
        {/* ---------- Public ---------- */}
        <Route index element={<LandingPage />} />
        <Route path={PATHS.UNAUTHORIZED} element={<UnauthorizedPage />} />

        {/* Public portfolio — open to anyone with the link, no session required. */}
        <Route path={PATHS.PUBLIC_PORTFOLIO} element={<PublicPortfolio />} />
        <Route path={PATHS.PUBLIC_PROJECT} element={<PublicProject />} />

        {/* ---------- Auth (guests only) ---------- */}
        <Route element={<PublicOnlyRoute />}>
          <Route element={<AuthLayout />}>
            <Route path={PATHS.LOGIN} element={<LoginPage />} />
            {/* Public registration removed — accounts are admin-provisioned. */}
            <Route path={PATHS.REGISTER} element={<Navigate to={PATHS.LOGIN} replace />} />
            <Route path={PATHS.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
          </Route>
        </Route>

        {/* ---------- Authenticated ---------- */}
        <Route element={<ProtectedRoute />}>
          {/* Join redirect — email "Join" links land here for every role */}
          <Route path={PATHS.JOIN_CLASS} element={<JoinClassPage />} />

          {/* Student */}
          <Route element={<RoleRoute allow={[ROLES.STUDENT]} />}>
            <Route element={<StudentLayout />}>
              <Route path={PATHS.STUDENT_ROOT} element={<Navigate to={PATHS.STUDENT_DASHBOARD} replace />} />
              <Route path={PATHS.STUDENT_DASHBOARD} element={<StudentDashboard />} />
              <Route path={PATHS.STUDENT_CLASSES} element={<StudentClasses />} />
              <Route path={PATHS.STUDENT_CALENDAR} element={<StudentCalendar />} />
              <Route path={PATHS.STUDENT_PROGRESS} element={<StudentProgress />} />
              <Route path={PATHS.STUDENT_RECORDS} element={<StudentRecords />} />
              <Route path={PATHS.STUDENT_MENTOR} element={<StudentMentor />} />
              <Route path={PATHS.STUDENT_AVAILABILITY} element={<StudentAvailability />} />
              <Route path={PATHS.STUDENT_REQUESTS} element={<StudentRequests />} />
              <Route path={PATHS.STUDENT_RESOURCES} element={<ResourcesPage />} />
              <Route path={PATHS.STUDENT_PORTFOLIO} element={<StudentPortfolio />} />
            </Route>
          </Route>

          {/* Mentor */}
          <Route element={<RoleRoute allow={[ROLES.MENTOR]} />}>
            <Route element={<MentorLayout />}>
              <Route path={PATHS.MENTOR_ROOT} element={<Navigate to={PATHS.MENTOR_DASHBOARD} replace />} />
              <Route path={PATHS.MENTOR_DASHBOARD} element={<MentorDashboard />} />
              <Route path={PATHS.MENTOR_STUDENTS} element={<MentorStudents />} />
              <Route path={PATHS.MENTOR_STUDENT} element={<MentorStudentDetail />} />
              <Route path={PATHS.MENTOR_CLASSES} element={<MentorClasses />} />
              <Route path={PATHS.MENTOR_CALENDAR} element={<MentorCalendar />} />
              <Route path={PATHS.MENTOR_AVAILABILITY} element={<MentorAvailability />} />
              <Route path={PATHS.MENTOR_SLOTS} element={<MentorOpenSlots />} />
              <Route path={PATHS.MENTOR_RECORDS} element={<MentorRecords />} />
              <Route path={PATHS.MENTOR_REQUESTS} element={<MentorRequests />} />
              <Route path={PATHS.MENTOR_PAYOUTS} element={<MentorPayouts />} />
              <Route path={PATHS.MENTOR_RESOURCES} element={<ResourcesPage />} />
              <Route path={PATHS.MENTOR_PORTFOLIO} element={<MentorPortfolioReview />} />
            </Route>
          </Route>

          {/* Admin */}
          <Route element={<RoleRoute allow={[ROLES.ADMIN]} />}>
            <Route element={<AdminLayout />}>
              <Route path={PATHS.ADMIN_ROOT} element={<Navigate to={PATHS.ADMIN_DASHBOARD} replace />} />
              <Route path={PATHS.ADMIN_DASHBOARD} element={<AdminDashboard />} />
              <Route path={PATHS.ADMIN_STUDENTS} element={<ManageStudents />} />
              <Route path={PATHS.ADMIN_STUDENT} element={<AdminStudentDetail />} />
              <Route path={PATHS.ADMIN_MENTORS} element={<ManageMentors />} />
              <Route path={PATHS.ADMIN_ACCOUNTS} element={<AccountsPage />} />
              <Route path={PATHS.ADMIN_COURSES} element={<ManageCourses />} />
              <Route path={PATHS.ADMIN_PRESENTATIONS} element={<ManagePresentations />} />
              <Route path={PATHS.ADMIN_ENROLLMENTS} element={<ManageEnrollments />} />
              {/* Student-centric view of the per-course mentor pairing. */}
              <Route path={PATHS.ADMIN_ENROL} element={<EnrolStudent />} />
              <Route path={PATHS.ADMIN_ASSIGN} element={<AssignMentor />} />
              <Route path={PATHS.ADMIN_SCHEDULE} element={<ScheduleClass />} />
              <Route path={PATHS.ADMIN_RESCHEDULE} element={<RescheduleClass />} />
              <Route path={PATHS.ADMIN_CALENDAR} element={<CalendarPage />} />
              <Route path={PATHS.ADMIN_SESSION_LOGS} element={<SessionLogsPage />} />
              <Route path={PATHS.ADMIN_DISPUTES} element={<DisputesPage />} />
              <Route path={PATHS.ADMIN_REPORTS} element={<ReportsPage />} />
              <Route path={PATHS.ADMIN_SETTINGS} element={<SchedulingSettings />} />
            </Route>
          </Route>
        </Route>

        {/* ---------- Fallback ---------- */}
        <Route path={PATHS.NOT_FOUND} element={<NotFoundPage />} />
      </Route>
      </Routes>
    </Suspense>
  );
}
